import { useQuery, type QueryClient } from '@tanstack/react-query';
import {
  BRAND_IMPORT_ACTIVE_STATUSES,
  BRAND_IMPORT_POLL_CEILING_MS,
  type BrandImportJobView,
  type BrandOnboardingView,
  type BrandProfileView,
} from '@mg/shared';
import { api } from '../../lib/api';

/**
 * The Brand Workspace v2 queries and their cache rules (final-design §2.14). Every key names the
 * workspace, so two workspaces never share an entry, and TenantProvider's switch invalidates them all.
 *
 * Every brand mutation answers with the whole BrandProfileView, carrying the new `version`: adopt it
 * (adoptBrandProfile) or the next save is a 409 against yourself.
 */
export const brandKeys = {
  /** The install's flag (useBrandFlags.ts). */
  flag: ['brand-flag'] as const,
  /** BrandProfileView. */
  profile: (tenantId: string) => ['brand', tenantId] as const,
  /** Every import query of a workspace: the latest job and each job by id. */
  imports: (tenantId: string) => ['brand-import', tenantId] as const,
  latestImport: (tenantId: string) => ['brand-import', tenantId, 'latest'] as const,
  importJob: (tenantId: string, jobId: string) => ['brand-import', tenantId, jobId] as const,
  copySources: (tenantId: string) => ['brand-copy-sources', tenantId] as const,
};

/** The wizard re-polls a running import this often (E12). */
export const WIZARD_IMPORT_POLL_MS = 2_000;
/** The Edit page's sources list re-polls a running import this often. */
export const EDIT_IMPORT_POLL_MS = 5_000;
/**
 * The wizard stops polling a job still running after this long and offers Try again (web.md §5.4): 13
 * minutes, past the API's deadline and its stale rule (BRAND_IMPORT_POLL_CEILING_MS).
 */
export const IMPORT_POLL_CEILING_MS = BRAND_IMPORT_POLL_CEILING_MS;
/** Copy sources change rarely: a minute. */
export const COPY_SOURCES_STALE_MS = 60_000;

/** Queued, parsing or extracting: the job is still running. */
export function isImportActive(job: Pick<BrandImportJobView, 'status'> | null | undefined): boolean {
  return !!job && BRAND_IMPORT_ACTIVE_STATUSES.includes(job.status);
}

export interface ImportPollOptions {
  /** Re-poll this often while the job is running; false (the default) never re-polls. */
  activePollMs?: number | false;
}

/** A workspace's brand information (`['brand', tenantId]`). Idle without a workspace id. */
export function useBrandProfile(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: brandKeys.profile(tenantId ?? ''),
    queryFn: ({ signal }) => api.getBrandProfile(tenantId as string, signal),
    enabled: !!tenantId,
  });
}

/**
 * The workspace's newest import (`['brand-import', tenantId, 'latest']`), `{ job: null }` when there is
 * none: the Edit page's sources list and the wizard's first merge. Re-polled while it runs when
 * `activePollMs` is given (EDIT_IMPORT_POLL_MS on the Edit page).
 */
export function useLatestBrandImport(tenantId: string | null | undefined, { activePollMs = false }: ImportPollOptions = {}) {
  return useQuery({
    queryKey: brandKeys.latestImport(tenantId ?? ''),
    queryFn: ({ signal }) => api.getLatestBrandImport(tenantId as string, signal),
    enabled: !!tenantId,
    refetchInterval: (query) =>
      activePollMs !== false && isImportActive(query.state.data?.job) ? activePollMs : false,
  });
}

/**
 * One import job (`['brand-import', tenantId, jobId]`), re-polled while it runs when `activePollMs` is
 * given (WIZARD_IMPORT_POLL_MS in the wizard). Idle without both ids. The ceiling (IMPORT_POLL_CEILING_MS)
 * is the caller's: pass `activePollMs: false` once it has passed.
 */
export function useBrandImportJob(
  tenantId: string | null | undefined,
  jobId: string | null | undefined,
  { activePollMs = false }: ImportPollOptions = {},
) {
  return useQuery({
    queryKey: brandKeys.importJob(tenantId ?? '', jobId ?? ''),
    queryFn: ({ signal }) => api.getBrandImportJob(tenantId as string, jobId as string, signal),
    enabled: !!tenantId && !!jobId,
    refetchInterval: (query) => (activePollMs !== false && isImportActive(query.state.data) ? activePollMs : false),
  });
}

/** The workspaces step 2 may copy Company voice and identity from (`['brand-copy-sources', tenantId]`). */
export function useCopySources(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: brandKeys.copySources(tenantId ?? ''),
    queryFn: ({ signal }) => api.listBrandCopySources(tenantId as string, signal),
    enabled: !!tenantId,
    staleTime: COPY_SOURCES_STALE_MS,
  });
}

/**
 * Adopts the view a brand mutation answered with (a step save, the Brand information save, either
 * dismissal), so the next save sends its `version`.
 */
export function adoptBrandProfile(queryClient: QueryClient, view: BrandProfileView): void {
  queryClient.setQueryData(brandKeys.profile(view.tenantId), view);
}

/** Adopts the onboarding state a position update answered with; nothing else in the view changes. */
export function adoptBrandOnboarding(queryClient: QueryClient, tenantId: string, onboarding: BrandOnboardingView): void {
  queryClient.setQueryData<BrandProfileView>(brandKeys.profile(tenantId), (prev) => (prev ? { ...prev, onboarding } : prev));
}

/** An import started or was retried: the workspace's latest job is a new one. */
export function refreshAfterImportStart(queryClient: QueryClient, tenantId: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: brandKeys.latestImport(tenantId) });
}

/** An import reached a terminal state (ready, partial or failed): the latest job and the brand view are read again. */
export async function refreshAfterImportEnd(queryClient: QueryClient, tenantId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: brandKeys.latestImport(tenantId) }),
    queryClient.invalidateQueries({ queryKey: brandKeys.profile(tenantId) }),
  ]);
}
